import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import GemstoneCard from "../components/GemstoneCard";
import CountdownTimer from "../components/CountdownTimer";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { useCurrency } from "../context/CurrencyContext";
import { useLang } from "../context/LanguageContext";
import Seo from "../components/Seo";
import { orgGraph, websiteSchema } from "../lib/schema";

// Hoisted: org + website schemas are static — compute once at module load
const HOME_JSON_LD = [...orgGraph(), websiteSchema()];

const Home = () => {
  const { format } = useCurrency();
  const { t } = useLang();
  const [gems, setGems] = useState([]);
  const [auctions, setAuctions] = useState([]);
  const [cert, setCert] = useState("");
  const [certResult, setCertResult] = useState(null);
  const [certError, setCertError] = useState("");

  useEffect(() => {
    api.get("/gemstones").then(({ data }) => setGems(data.slice(0, 6))).catch(() => {});
    api.get("/auctions", { params: { status: "live" } }).then(({ data }) => setAuctions(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verify = async (e) => {
    e.preventDefault();
    setCertResult(null); setCertError("");
    try {
      const { data } = await api.get(`/certificates/verify/${cert.trim().toUpperCase()}`);
      setCertResult(data);
    } catch (err) {
      setCertError(err.response?.data?.detail || "Certificate not found");
    }
  };

  const featured = auctions[0];

  return (
    <div data-testid="home-page">
      <Seo
        title="Luxury Gemstones · Auctions · Investment-Grade · Jakarta"
        description="OPIKJEWELS — Southeast Asian luxury gemstone house. Investment-grade natural diamonds, Kashmir sapphires, Mogok rubies, Muzo emeralds, Argyle pinks. Live auctions, certified provenance, lifetime warranty. Jakarta · Bangkok."
        path="/"
        jsonLd={HOME_JSON_LD}
      />
      {/* HERO */}
      <section className="relative h-[100vh] overflow-hidden grain">
        <img
          src="https://images.unsplash.com/photo-1777126413608-f06fbd6e3208"
          alt="OPIKJEWELS"
          className="absolute inset-0 w-full h-full object-cover scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-black"></div>
        <div className="relative z-10 max-w-[1600px] mx-auto h-full px-6 md:px-12 lg:px-16 flex flex-col justify-end pb-24 md:pb-32">
          <div className="reveal-up">
            <p className="font-accent text-[11px] md:text-sm tracking-[0.4em] uppercase text-gold mb-6">
              {t("home_eyebrow")}
            </p>
            <h1 className="font-heading font-light tracking-tight text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.95] max-w-5xl">
              {t("home_title_1")}<br /><span className="italic text-ivory">{t("home_title_2")}</span>
            </h1>
            <p className="mt-8 max-w-xl text-white/70 text-base md:text-lg font-light leading-relaxed">
              {t("home_lede")}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/auctions" data-testid="hero-auction-cta" className="bg-gold text-black font-accent uppercase tracking-[0.28em] text-xs px-9 py-4 hover:bg-ivory transition-colors duration-500">
                {t("cta_enter_auction")}
              </Link>
              <Link to="/collection" data-testid="hero-collection-cta" className="border border-white/30 text-white font-accent uppercase tracking-[0.28em] text-xs px-9 py-4 hover:border-gold hover:text-gold transition-colors duration-500">
                {t("cta_view_collection")}
              </Link>
            </div>
          </div>
        </div>
        <div className="absolute bottom-6 right-6 md:right-12 z-10 font-accent text-[10px] tracking-[0.28em] uppercase text-white/40">
          Jakarta · Bangkok
        </div>
      </section>

      {/* SIGNATURE COLLECTIONS */}
      <section className="py-32 md:py-40 max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div>
            <p className="font-accent text-[11px] tracking-[0.28em] uppercase text-gold mb-5">Signature Collections</p>
            <h2 className="font-heading font-light text-4xl md:text-6xl leading-tight max-w-2xl">A curated portfolio of exceptional gemstones.</h2>
          </div>
          <Link to="/collection" className="font-accent text-[11px] tracking-[0.28em] uppercase text-white/60 hover:text-gold flex items-center gap-2">
            All Collections <ArrowRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
          {gems.map((g) => <GemstoneCard key={g.id} gem={g} />)}
        </div>
      </section>

      {/* LIVE AUCTION TEASER */}
      {featured && (
        <section className="bg-onyx grain relative">
          <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 py-32 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative aspect-[5/6] overflow-hidden bg-black">
              <img src={featured.gemstone?.image_url} alt={featured.title} className="w-full h-full object-cover" />
              <div className="absolute top-5 left-5 font-accent text-[10px] tracking-[0.28em] uppercase text-black bg-gold px-3 py-1.5">Live Now</div>
            </div>
            <div>
              <p className="font-accent text-[11px] tracking-[0.28em] uppercase text-gold mb-5">Featured Lot · The Jakarta Sale</p>
              <h2 className="font-heading font-light text-4xl md:text-5xl mb-6">{featured.title}</h2>
              <p className="text-white/60 max-w-lg leading-relaxed mb-10">{featured.gemstone?.name} — {featured.gemstone?.carat} ct, {featured.gemstone?.origin}.</p>
              <div className="flex flex-wrap gap-12 mb-10">
                <div>
                  <div className="font-accent text-[10px] tracking-[0.28em] uppercase text-white/40 mb-2">{t("label_current_bid")}</div>
                  <div className="font-heading text-3xl text-ivory">{format(featured.current_bid)}</div>
                </div>
                <div>
                  <div className="font-accent text-[10px] tracking-[0.28em] uppercase text-white/40 mb-2">Bids</div>
                  <div className="font-heading text-3xl text-ivory">{featured.bid_count}</div>
                </div>
              </div>
              <CountdownTimer endsAt={featured.ends_at} />
              <Link to={`/auctions/${featured.id}`} data-testid="featured-bid-cta" className="mt-10 inline-block bg-gold text-black font-accent uppercase tracking-[0.28em] text-xs px-9 py-4 hover:bg-ivory transition-colors duration-500">
                {t("cta_place_bid")}
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CERTIFICATE VERIFY */}
      <section className="py-32 md:py-40 max-w-3xl mx-auto px-6 text-center">
        <ShieldCheck className="mx-auto text-gold mb-6" size={36} />
        <p className="font-accent text-[11px] tracking-[0.28em] uppercase text-gold mb-5">Certificate & Warranty Portal</p>
        <h2 className="font-heading font-light text-4xl md:text-5xl mb-6">Verify the provenance of any OPIKJEWELS stone.</h2>
        <p className="text-white/60 mb-10 leading-relaxed">Enter your certificate number to access full provenance, treatment disclosure, and lifetime warranty status.</p>
        <form onSubmit={verify} className="flex flex-col sm:flex-row gap-3 justify-center">
          <input
            data-testid="cert-lookup-input"
            value={cert}
            onChange={(e) => setCert(e.target.value)}
            placeholder="OPK-DIAM-001"
            className="bg-transparent border-b border-white/30 text-white px-2 py-3 focus:outline-none focus:border-gold font-accent tracking-[0.2em] uppercase text-sm w-full sm:w-80 placeholder-white/30 text-center"
          />
          <button data-testid="cert-lookup-submit" type="submit" className="border border-gold text-gold font-accent uppercase tracking-[0.28em] text-xs px-9 py-4 hover:bg-gold hover:text-black transition-colors duration-500">
            Verify
          </button>
        </form>
        {certResult && (
          <div data-testid="cert-result" className="mt-10 text-left bg-onyx border border-gold/30 p-8">
            <div className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold mb-3">Verified · Authentic</div>
            <div className="font-heading text-3xl mb-4">{certResult.gemstone_name}</div>
            <div className="grid grid-cols-2 gap-4 text-sm text-white/70">
              <div><span className="text-white/40">Origin:</span> {certResult.origin}</div>
              <div><span className="text-white/40">Carat:</span> {certResult.carat}</div>
              <div><span className="text-white/40">Treatment:</span> {certResult.treatment}</div>
              <div><span className="text-white/40">Lab:</span> {certResult.issuing_lab}</div>
              <div><span className="text-white/40">Warranty:</span> {certResult.warranty_status}</div>
              <div><span className="text-white/40">Owner:</span> {certResult.owner_name || "—"}</div>
            </div>
          </div>
        )}
        {certError && <div data-testid="cert-error" className="mt-6 text-red-400 text-sm">{certError}</div>}
        <div className="mt-12 flex justify-center gap-8 font-accent text-[10px] tracking-[0.28em] uppercase text-white/30">
          <span>GIA</span><span>Gübelin</span><span>SSEF</span><span>AGL</span><span>GRS</span>
        </div>
      </section>

      {/* HERITAGE */}
      <section className="bg-onyx grain relative py-32 md:py-40">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="font-accent text-[11px] tracking-[0.28em] uppercase text-gold mb-5">Heritage</p>
            <h2 className="font-heading font-light text-4xl md:text-6xl leading-[1.05] mb-8">A legacy <span className="italic">of rarity.</span></h2>
            <div className="gold-line w-24 mb-8"></div>
            <p className="text-white/60 leading-relaxed mb-6">For three decades, OPIKJEWELS has served collectors, investors, and royal houses with the world's most exceptional gemstones. Every stone is hand-selected, certified, and accompanied by full lifetime provenance.</p>
            <p className="text-white/60 leading-relaxed">Our concierge connects discerning clients to the rarest specimens — no-heat Kashmir sapphires, Type IIa diamonds, Mogok pigeon-blood rubies — privately, discreetly, globally.</p>
            <Link to="/about" className="mt-10 inline-block border border-white/30 text-white font-accent uppercase tracking-[0.28em] text-xs px-9 py-4 hover:border-gold hover:text-gold transition-colors duration-500">
              The Maison
            </Link>
          </div>
          <div className="aspect-[4/5] overflow-hidden">
            <img src="https://images.pexels.com/photos/33257666/pexels-photo-33257666.jpeg" alt="Atelier" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
